import { Player } from "./util/Player.js";
import { S } from "./Squaremap.js";

class PlayerList {
    /** @type {Map<string, Player>} */
    players;
    /** @type {Map} */
    markers;
    /** @type {string | null} */
    following;
    /** @type {boolean} */
    firstTick;
    /** @type {string} */
    label;
    /** @type {PlayersData | null} */
    jsonCache;

    /**
     * @param {Settings_UI_Sidebar} json
     */
    constructor(json) {
        this.players = new Map();
        this.markers = new Map();
        this.jsonCache = null;
        this.following = null;
        this.firstTick = true;
        this.label = json.player_list_label;
        S.map.createPane("nameplate").style.zIndex = 1000;
        // player heads sit BELOW all markers (markerPane is 600)
        S.map.createPane("agonia-players").style.zIndex = 580;
        // the "flashlight" cones sit below the heads and never take clicks
        const beamPane = S.map.createPane("agonia-beam");
        beamPane.style.zIndex = 560;
        beamPane.style.pointerEvents = "none";
    }
    tick() {
        const update = () => {
            this.updatePlayerList(this.jsonCache.players);
            // Always Swedish + total online count only (works on stock squaremap,
            // whose label would otherwise be the English "Players ({cur}/{max})").
            const count = this.jsonCache.players.length;
            const title = `Spelare ( ${count} )`;
            if (S.sidebar.players.legend.innerHTML !== title) {
                S.sidebar.players.legend.innerHTML = title;
            }
        };
        const fetchPlayers = (callback) => {
            S.getJSON(
                "tiles/players.json",
                /** @param {PlayersData} json */
                (json) => {
                    this.jsonCache = json;
                    callback();
                },
                true,
            );
        };

        if (S.tick_count % S.worldList.curWorld.player_tracker.update_interval === 0) {
            if (S.staticMode) {
                if (this.jsonCache === null) {
                    fetchPlayers(() => update());
                } else {
                    update();
                }
            } else {
                fetchPlayers(() => update());
            }
        }
    }
    /**
     * @param {string} uuid
     */
    showPlayer(uuid) {
        const player = this.players.get(uuid);
        if (!S.worldList.worlds.has(player.world)) {
            return false;
        }
        S.worldList.showWorld(player.world, () => {
            S.map.panTo(S.toLatLng(player.x, player.z));
        });
        return true;
    }
    /**
     * @param {Player} player
     */
    addToList(player) {
        const head = document.createElement("img");
        head.classList.add("head");
        head.src = player.getHeadUrl();

        const span = document.createElement("span");
        span.textContent = player.displayName.replace(/<[^>]*>/g, "");

        const link = S.createElement("a", player.uuid, this);
        link.dataset.name = player.name; // plain MC name, used to pin "me" to the top
        link.onclick = function (e) {
            if (this.parent.showPlayer(this.id)) {
                this.parent.followPlayerMarker(this.id);
                e.stopPropagation();
            }
        };
        link.appendChild(head);
        link.appendChild(span);
        S.sidebar.players.element.appendChild(link);
        this.sortList();
    }

    /** Sort the player list alphabetically, but pin the logged-in player to the top
     *  and highlight their row. */
    sortList() {
        const fieldset = S.sidebar.players.element;
        const me = (window.agoniaMe || "").toLowerCase();
        const links = Array.from(fieldset.getElementsByTagName("a"));
        links
            .sort((a, b) => {
                const an = (a.dataset.name || plain(a.getElementsByTagName("span")[0])).toLowerCase();
                const bn = (b.dataset.name || plain(b.getElementsByTagName("span")[0])).toLowerCase();
                const am = me && an === me ? 0 : 1;
                const bm = me && bn === me ? 0 : 1;
                return am !== bm ? am - bm : an.localeCompare(bn);
            })
            .forEach((link) => {
                const nm = (link.dataset.name || "").toLowerCase();
                link.classList.toggle("agonia-me", !!me && nm === me);
                fieldset.appendChild(link);
            });
    }

    /** Re-apply the "me at the top" ordering (called when login state changes). */
    reorderMe() {
        this.sortList();
    }

    /** Push the settings-menu display options (always-on names, flashlight) onto
     *  every player head that already exists. */
    applyDisplaySettings() {
        for (const player of this.players.values()) {
            player.bindName();
            player.syncBeam();
        }
    }
    /**
     * @param {Player} player
     */
    removeFromList(player) {
        const link = document.getElementById(player.uuid);
        if (link != null) {
            link.remove();
        }
        this.players.delete(player.uuid);
        player.removeMarker();
    }
    /**
     * @param {PlayerData[]} players
     */
    updatePlayerList(players) {
        const playersToRemove = Array.from(this.players.keys());

        let needsSort = false;

        // update players from json
        for (let i = 0; i < players.length; i++) {
            let player = this.players.get(players[i].uuid);
            if (player == null) {
                // new player
                player = new Player(players[i]);
                this.players.set(player.uuid, player);
                this.addToList(player);
                // The constructor builds the head marker but does NOT put it on the
                // map — only update() does (via addMarker). Without this call a
                // just-joined player, and everyone already online at page load, has
                // no head until the NEXT poll. Run it now so the head shows at once.
                player.update(players[i]);
            } else {
                const oldDisplayName = player.displayName;
                player.update(players[i]);
                if (oldDisplayName !== player.displayName) {
                    needsSort = true;
                    document.getElementById(player.uuid).getElementsByTagName("span")[0].textContent =
                        player.displayName.replace(/<[^>]*>/g, "");
                }
            }
            playersToRemove.remove(players[i].uuid);
        }

        // remove players not in json
        for (let i = 0; i < playersToRemove.length; i++) {
            const player = this.players.get(playersToRemove[i]);
            this.removeFromList(player);
        }

        if (needsSort) {
            this.sortList();
        }

        // first tick only
        if (this.firstTick) {
            this.firstTick = false;

            // follow uuid from url
            const follow = S.getUrlParam("uuid", null);
            if (follow != null && this.players.get(follow) != null) {
                this.followPlayerMarker(follow);
            }
        }

        // follow highlighted player
        if (this.following != null) {
            const player = this.players.get(this.following);
            if (player != null && S.worldList.curWorld != null) {
                if (player.world !== S.worldList.curWorld.name) {
                    S.worldList.showWorld(player.world, () => {
                        S.map.panTo(S.toLatLng(player.x, player.z));
                    });
                } else {
                    // Glide the view to the head at the same speed the head glides
                    // (linear, over the player update interval) so it tracks smoothly
                    // instead of jumping each tick.
                    const pt = S.worldList.curWorld.player_tracker;
                    const dur = pt && pt.update_interval ? Number(pt.update_interval) : 1;
                    S.map.panTo(S.toLatLng(player.x, player.z), {
                        animate: true,
                        duration: dur > 0 ? dur : 1,
                        easeLinearity: 1,
                    });
                }
            }
        }
    }
    clearPlayerMarkers() {
        const playersToRemove = Array.from(this.players.keys());
        for (let i = 0; i < playersToRemove.length; i++) {
            const player = this.players.get(playersToRemove[i]);
            player.removeMarker();
        }
        this.markers.clear();
        // S.layerControl.playersLayer.clearLayers();
    }
    /**
     * @param {string} uuid
     */
    followPlayerMarker(uuid) {
        if (this.following !== null && this.following !== uuid) {
            document.getElementById(this.following).classList.remove("following");
        }
        this.following = uuid;
        if (this.following != null) {
            document.getElementById(this.following).classList.add("following");
        }
    }
}

/**
 * @param {HTMLElement} element
 */
function plain(element) {
    return element.textContent || element.innerText || "";
}

export { PlayerList };
